import {
  EventCalendarBlockEntity,
  EventCalendarBlockPriceEntity,
  EventCalendarEntity,
} from "../../../proto/generated/api_entities_pb";
import {IEventCalendar} from "./eventCalendarReducerTypes";
import {parseStringAsUTCDate} from "../../../utils/helpers/dateHelpers/dateHelpers";
import {isValidDate} from "../../../utils/helpers/dateHelpers/dateHelpers";
import {mapTimeZoneEntityToITimezone} from "../fleetReducer/fleetMappers";

export const mapEventCalendarEntityToIEventCalendar = (item: EventCalendarEntity): IEventCalendar => {
  const eventCalendarId = item.getEventcalendarid();
  if (!eventCalendarId) {
    throw new Error(`Invalid eventCalendarId in received EventCalendarEntity`);
  }

  const eventName = item.getEventname();
  if (!eventName) {
    throw new Error(`Invalid eventName in received EventCalendarEntity`);
  }

  const dateFrom = item.getDatefrom();
  if (!dateFrom) {
    throw new Error(`Invalid dateFrom in received EventCalendarEntity`);
  }

  const dateTo = item.getDateto();
  if (!dateTo) {
    throw new Error(`Invalid dateTo in received EventCalendarEntity`);
  }

  const source = item.getSource();
  if (!source || (source !== 'system' && source !== 'operator_admin')) {
    throw new Error(`Invalid source in received EventCalendarEntity: ${source}`);
  }

  const createdAt = item.getCreatedat();
  if (!createdAt) {
    throw new Error(`Invalid createdAt in received EventCalendarEntity`);
  }

  const updatedAt = item.getUpdatedat();
  if (!updatedAt) {
    throw new Error(`Invalid updatedAt in received EventCalendarEntity`);
  }

  const result: IEventCalendar = {
    eventCalendarId,
    eventName,
    dateFrom,
    dateTo,
    source: source as 'system' | 'operator_admin',
    createdAt,
    updatedAt,
  };

  if (item.hasCreatedbyuserid()) {
    result.createdByUserId = item.getCreatedbyuserid();
  }

  if (item.hasSeasontype() && item.getSeasontype()) {
    result.seasonType = item.getSeasontype() as 'high' | 'low';
  }

  // Всегда пытаемся получить color, даже если hasColor() возвращает false
  const color = item.getColor();
  if (color) {
    result.color = color;
  }

  // Обрабатываем блоки, если они есть
  if (item.getBlocksList().length > 0) {
    const blocks: Array<{
      airportIds?: number[] | null;
      priceLight?: number;
      currencyLightId?: number;
      priceMidsize?: number;
      currencyMidsizeId?: number;
    }> = [];
    
    // Группируем блоки по аэропортам для объединения Light/Midsize
    const groupedBlocks: Record<string, {
      airportIds?: number[] | null;
      prices?: Array<{
        planeTypeId: number;
        currencyId: number;
        price: number;
      }>;
      priceLight?: number;
      currencyLightId?: number;
      priceMidsize?: number;
      currencyMidsizeId?: number;
    }> = {};
    
    item.getBlocksList().forEach((blockEntity: any) => {
      // Получаем airportIds (массив или null)
      const airportIds = blockEntity.getAirportidsList();
      const key = airportIds && airportIds.length > 0 ? airportIds.join(',') : 'all';
      
      if (!groupedBlocks[key]) {
        groupedBlocks[key] = {
          airportIds: airportIds && airportIds.length > 0 ? airportIds : (airportIds.length === 0 ? null : undefined),
        };
      }
      
      // Обрабатываем новую структуру с prices массивом
      const pricesList = blockEntity.getPricesList();
      if (pricesList && pricesList.length > 0) {
        // Инициализируем массив prices, если его еще нет
        if (!groupedBlocks[key].prices) {
          groupedBlocks[key].prices = [];
        }
        
        pricesList.forEach((priceEntity: any) => {
          const planeTypeId = priceEntity.getPlanetypeid();
          const currencyId = priceEntity.getCurrencyid();
          const price = parseFloat(priceEntity.getPrice());
          
          // Добавляем все цены в массив prices
          groupedBlocks[key].prices!.push({
            planeTypeId: planeTypeId,
            currencyId: currencyId,
            price: price,
          });
          
          // Сохраняем также в старые поля для обратной совместимости
          if (planeTypeId === 1) {
            // Light
            groupedBlocks[key].priceLight = price;
            groupedBlocks[key].currencyLightId = currencyId;
          } else if (planeTypeId === 2) {
            // Midsize
            groupedBlocks[key].priceMidsize = price;
            groupedBlocks[key].currencyMidsizeId = currencyId;
          }
        });
      } else {
        // Обратная совместимость: обрабатываем старую структуру priceLight и priceMidsize
        if (!groupedBlocks[key].prices) {
          groupedBlocks[key].prices = [];
        }
        
        if (blockEntity.hasPricelight() && blockEntity.getPricelight()) {
          const price = parseFloat(blockEntity.getPricelight());
          groupedBlocks[key].priceLight = price;
          groupedBlocks[key].currencyLightId = blockEntity.getCurrencylightid();
          groupedBlocks[key].prices!.push({
            planeTypeId: 1,
            currencyId: blockEntity.getCurrencylightid(),
            price: price,
          });
        }
        
        if (blockEntity.hasPricemidsize() && blockEntity.getPricemidsize()) {
          const price = parseFloat(blockEntity.getPricemidsize());
          groupedBlocks[key].priceMidsize = price;
          groupedBlocks[key].currencyMidsizeId = blockEntity.getCurrencymidsizeid();
          groupedBlocks[key].prices!.push({
            planeTypeId: 2,
            currencyId: blockEntity.getCurrencymidsizeid(),
            price: price,
          });
        }
      }
    });
    
    result.blocks = Object.values(groupedBlocks);
  }

  return result;
};

export const mapIEventCalendarToEventCalendarEntity = (item: IEventCalendar): EventCalendarEntity => {
  const entity = new EventCalendarEntity();
  entity.setEventcalendarid(item.eventCalendarId);
  entity.setEventname(item.eventName);
  entity.setDatefrom(item.dateFrom);
  entity.setDateto(item.dateTo);
  entity.setSource(item.source);

  if (item.createdByUserId !== undefined) {
    entity.setCreatedbyuserid(item.createdByUserId);
  }

  if (item.seasonType !== undefined) {
    entity.setSeasontype(item.seasonType);
  }

  if (item.color !== undefined) {
    entity.setColor(item.color);
  }

  // Добавляем блоки - они обязательны и не должны быть пустыми
  if (item.blocks && item.blocks.length > 0) {
    item.blocks.forEach(block => {
      const blockEntity = new EventCalendarBlockEntity();
      
      // Устанавливаем airportIds (массив или null)
      // Если airportIds === null или undefined, не добавляем ничего (в прото будет пустой массив, что означает null)
      // Если airportIds === [], не добавляем ничего (пустой массив означает null - для всех аэропортов)
      // Если airportIds содержит значения, добавляем их
      if (block.airportIds && block.airportIds.length > 0) {
        block.airportIds.forEach(airportId => {
          blockEntity.addAirportids(airportId);
        });
      }
      // Если airportIds === null, undefined или пустой массив, не добавляем ничего
      // В прото пустой массив airportIds означает null (для всех аэропортов)
      
      // Используем новую структуру с prices - она обязательна и не должна быть пустой
      if (block.prices && block.prices.length > 0) {
        block.prices.forEach(price => {
          const priceEntity = new EventCalendarBlockPriceEntity();
          priceEntity.setPlanetypeid(price.planeTypeId);
          priceEntity.setCurrencyid(price.currencyId);
          priceEntity.setPrice(price.price.toString());
          blockEntity.addPrices(priceEntity);
        });
      } else {
        // Обратная совместимость: преобразуем старую структуру в новую
        const prices: Array<{planeTypeId: number; currencyId: number; price: number}> = [];
        
        if (block.priceLight !== undefined && block.currencyLightId !== undefined) {
          prices.push({
            planeTypeId: 1, // Light
            currencyId: block.currencyLightId,
            price: block.priceLight,
          });
        }
        
        if (block.priceMidsize !== undefined && block.currencyMidsizeId !== undefined) {
          prices.push({
            planeTypeId: 2, // Midsize
            currencyId: block.currencyMidsizeId,
            price: block.priceMidsize,
          });
        }
        
        // Добавляем prices только если они есть
        if (prices.length > 0) {
          prices.forEach(price => {
            const priceEntity = new EventCalendarBlockPriceEntity();
            priceEntity.setPlanetypeid(price.planeTypeId);
            priceEntity.setCurrencyid(price.currencyId);
            priceEntity.setPrice(price.price.toString());
            blockEntity.addPrices(priceEntity);
          });
        }
      }
      
      // Добавляем блок только если есть хотя бы одна цена
      if (blockEntity.getPricesList().length > 0) {
        entity.addBlocks(blockEntity);
      }
    });
  }

  return entity;
};

